<script lang="ts">
  import type { HTMLAttributes } from 'vue'
  import { createContext } from 'reka-ui'

  const [_useDataListItem, provideDataListItem] = createContext<{
    variant?: 'default' | 'secondary'
  }>('gs-data-list-item')

  export function useDataListItem() {
    try {
      return _useDataListItem()
    } catch (error) {
      return {
        variant: 'default'
      }
    }
  }
  export interface DataListItemProps<T = unknown> {
    item?: T
    /** Vue Router target. If provided, item becomes a RouterLink. */
    to?: GatRouteLocation<AppRoute>
    /** If provided, item becomes an anchor tag. */
    href?: string
    disabled?: boolean
    selected?: boolean
    options?: {
      showChevron?: boolean
      clickable?: boolean
    }
    class?: HTMLAttributes['class']
    contentClass?: HTMLAttributes['class']
    variant?: 'default' | 'secondary'
  }
</script>

<script setup lang="ts" generic="T">
  import { computed, onMounted, reactive, useSlots } from 'vue'
  import { cn } from '@gat-ship/vue-components'
  import {
    type GatRouteLocation,
    type AppRoute,
    RouterLink
  } from '@gat-ship/lib/router'
  import { globalUtils } from '@gat-ship/utils'

  const props = withDefaults(defineProps<DataListItemProps<T>>(), {
    disabled: false,
    selected: false,
    clickable: undefined,
    variant: 'default'
  })

  const emit = defineEmits<{
    (e: 'click:item', item: T | undefined, event: MouseEvent): void
  }>()

  const tag = computed(() => {
    if (props.to) return RouterLink
    if (props.href) return 'a'
    if (hasEventBound.click) return 'button'
    // If it's clickable (emits click) but not a link, prefer button for a11y
    return 'div'
  })

  function onClick(event: MouseEvent) {
    if (props.disabled) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    emit('click:item', props.item, event)
  }

  const hasEventBound = reactive({
    click: false
  })

  const isClickable = computed(() => {
    if (typeof props.options?.clickable === 'boolean')
      return props.options.clickable
    if (props.disabled) return false
    if (props.to) return true
    if (props.href) return true
    if (hasEventBound.click) return true
    return false
  })

  const slots = useSlots()

  const showChevron = computed(() => {
    if (props.options?.showChevron) {
      return props.options.showChevron
    }
    return props.to && !slots.actions
  })

  onMounted(() => {
    hasEventBound.click =
      globalUtils.functions.components.hasEmitListener('click:item')
  })

  provideDataListItem({
    variant: props.variant
  })
</script>

<template>
  <component
    :is="tag"
    v-bind="
      props.to
        ? { to: props.to }
        : props.href
          ? { href: props.href }
          : { type: 'button' }
    "
    :disabled="tag === 'button' ? props.disabled : undefined"
    :aria-disabled="
      tag !== 'button' ? (props.disabled ? 'true' : undefined) : undefined
    "
    :tabindex="props.disabled && tag !== 'button' ? -1 : undefined"
    @click="onClick"
    :class="
      cn(
        // base
        'group flex w-full items-start gap-2 text-left',
        // spacing (you can later turn this into density)
        'p-6',
        {
          // interaction
          'hover:bg-accent focus-visible:bg-accent cursor-pointer': isClickable,
          'bg-background-lighter hover:bg-background-light':
            props.variant === 'secondary'
        },
        'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        // states
        props.selected && 'bg-accent',
        props.disabled && 'cursor-not-allowed opacity-60',
        props.class
      )
    "
  >
    <div v-if="$slots.leading" class="h-full shrink-0">
      <slot name="leading" />
    </div>

    <div
      :class="
        cn(
          'flex h-full min-h-0 w-full min-w-0 grow flex-col items-start justify-center overflow-hidden',
          props.contentClass
        )
      "
    >
      <slot />
    </div>

    <div v-if="$slots.meta" class="shrink-0">
      <slot name="meta" />
    </div>

    <div v-if="$slots.actions || props.to" class="shrink-0">
      <slot name="actions" />
    </div>
    <div
      v-if="showChevron"
      class="flex h-full flex-1 shrink-0 items-center justify-center self-center text-neutral-400 group-hover:text-neutral-600"
    >
      <gat-icon
        icon="ChevronRight"
        class="size-5 transition duration-200 ease-out group-hover:translate-x-1"
      />
    </div>
  </component>
</template>
